import PropTypes from 'prop-types';
import { makeStyles, Typography } from '@material-ui/core';
import classNames from 'classnames';
import Candidate from '../../candidatesList/candidate';
import { ReactComponent as VoteAgainstIcon } from '../../../../assets/icons/thumb_down_alt.svg';
import { ReactComponent as VoteForIcon } from '../../../../assets/icons/thumb_up_alt.svg';
import RichTextPreview from '../../richTextPreview';
import {
  getDifferenceInDays,
  formatDate,
  areEqualInMilliseconds,
} from '../../../../utility/dateUtils';
import { VOTE_MAP } from '../../voteField/config';
import DotsMenu from '../../dotsMenu';
import ConditionalTooltip from '../../conditionalTooltip';
import { EVENT_DATE_TIME_FORMAT } from '../jobEvent/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  candidate: {
    marginTop: spacing(4),
  },
  infoWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  commentBlock: {
    borderBottom: `1px solid ${primary.bluish7}`,
    paddingBottom: spacing(4),
  },
  reviewAvatarWrapper: {
    margin: 0,
    backgroundColor: primary.white,
    padding: 0,
  },
  thumbUpIcon: {
    minWidth: 'fit-content',
    marginRight: spacing(3),
    '& g, & path': {
      fill: primary.green1,
    },
  },
  thumbDownIcon: {
    minWidth: 'fit-content',
    marginRight: spacing(3),
    '& g, & path': {
      fill: primary.red1,
    },
  },
  voteCandidateWrapper: {
    marginTop: spacing(5),
    marginBottom: spacing(4),
    justifyContent: 'space-between',
  },
  commentWrapper: {
    marginLeft: spacing(9),
  },
  disabledIcon: {
    opacity: 0.3,
  },
  disabledAction: {
    cursor: 'pointer',
  },
  dateLabel: {
    marginRight: spacing(2),
  },
  wrapper: {
    minWidth: 'fit-content',
  },
}));

const VoteInfo = ({
  translations,
  record,
  menuItems = [],
  tooltipText = '',
  isDisabledDotsMenu = false,
}) => {
  const classes = useStyles();
  const numberOfDays = Math.abs(getDifferenceInDays(record.created_at));
  const daysAgo = numberOfDays
    ? `${numberOfDays} ${numberOfDays === 1 ? 'day' : 'days'} ago`
    : 'today';
  const createdAtDate = formatDate(record.created_at, EVENT_DATE_TIME_FORMAT);
  const isEdited = areEqualInMilliseconds(record.updated_at, record.created_at);
  const createdAt = isEdited
    ? `${createdAtDate} ${translations.edited}`
    : createdAtDate;

  return (
    <div className={classes.commentBlock}>
      <div
        className={classNames(
          classes.infoWrapper,
          classes.voteCandidateWrapper
        )}
      >
        <div className={classes.infoWrapper}>
          {record.vote === VOTE_MAP.APPROVE ? (
            <VoteForIcon className={classes.thumbUpIcon} />
          ) : (
            <VoteAgainstIcon className={classes.thumbDownIcon} />
          )}
          <Candidate
            className={classNames(
              classes.candidate,
              classes.reviewAvatarWrapper
            )}
            user={record.reviewer}
            label={daysAgo}
            hasLabel
          />
        </div>
        <div className={classNames(classes.infoWrapper, classes.wrapper)}>
          <Typography
            variant="overline"
            className={(classes.infoWrapper, classes.dateLabel)}
          >
            {createdAt}
          </Typography>
          <ConditionalTooltip
            className={classes.disabledAction}
            message={tooltipText}
            addTooltip={isDisabledDotsMenu}
          >
            <DotsMenu
              menuItems={menuItems}
              isDisabled={isDisabledDotsMenu}
              iconButtonClass={classNames({
                [classes.disabledIcon]: isDisabledDotsMenu,
              })}
            />
          </ConditionalTooltip>
        </div>
      </div>
      <RichTextPreview
        variant="body1"
        className={classNames(classes.commentWrapper)}
        text={record.comment}
      />
    </div>
  );
};

VoteInfo.propTypes = {
  translations: PropTypes.shape({}).isRequired,
  record: PropTypes.object.isRequired,
  menuItems: PropTypes.arrayOf(PropTypes.shape({})),
  tooltipText: PropTypes.string,
  isDisabledDotsMenu: PropTypes.bool,
};

export default VoteInfo;
